import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Flipped, Flipper, spring } from 'react-flip-toolkit';
import { useHistory } from 'react-router-dom';
import { connect } from 'unistore/full/react';
import actions from './actions';
import truncate from './truncate';
import './Queue.scss';
import spfetch from './spfetch';
import getTypeFromUri from './get-type-from-uri';

const onAppear = (el, index) =>
  spring({
    onUpdate: (val) => {
      el.style.opacity = val;
    },
  });

const onExit = (el, index, removeElement) => {
  spring({
    config: { overshootClamping: true },
    onUpdate: (val) => {
      el.style.opacity = 1 - val;
    },
    onComplete: removeElement,
  });

  return () => {
    el.style.opacity = '';
    removeElement();
  };
};

export default connect(
  ({
    tracksByUri,
    playerQueue,
    currentRow,
    playerState,
    contextUri,
    isPremium,
    isShuffleEnabled,
    playlistsById,
  }) => ({
    currentTrack: {
      ...currentRow?.track,
      isRecommendation: playerQueue?.current_track?.metadata?.seed === 'false',
    },
    nextTracks: playerQueue?.next_tracks?.map((playerQueueTrack) => ({
      ...(tracksByUri[playerQueueTrack?.uri] || playerQueueTrack),
      isRecommendation: playerQueueTrack?.metadata?.seed === 'false',
    })),
    contextName:
      playerState?.context?.metadata?.context_description ||
      (getTypeFromUri(contextUri) === 'playlist' &&
        playlistsById[contextUri?.substr(-22)]?.name),
    isPlaying: !playerState?.paused && !!contextUri,
    isPremium,
    isShuffleEnabled,
    playerQueue,
  }),
  actions
)(function Queue(props) {
  const {
    contextName,
    currentTrack,
    nextTracks,
    onClose,
    isPlaying,
    isPremium,
    isShuffleEnabled,
  } = props;
  const currentTrackImageUrl = currentTrack?.album?.images?.[0]?.url;

  const artists = useMemo(
    () =>
      Object.values(
        nextTracks.reduce((artists, track) => {
          const artist = track?.artists?.[0];
          artists[artist?.uri] = artist;
          return artists;
        }, {})
      )
        .filter(Boolean)
        .sort((a, b) => a?.name?.localeCompare(b?.name))
        .slice(0, 30),
    [nextTracks?.map((track) => track?.uri).join('')]
  );

  const [artistImagesById, setArtistImagesById] = useState({});
  useEffect(() => {
    const artistsThatNeedDecoration = artists.filter(
      (artist) => artist?.id && !(artist.id in artistImagesById)
    );
    if (artistsThatNeedDecoration.length) {
      spfetch(
        `/v1/artists?ids=${artistsThatNeedDecoration
          .map((artist) => artist.id)
          .join(',')}`
      ).then(({ artists }) =>
        setArtistImagesById({
          ...artistImagesById,
          ...artists.reduce((newArtistImagesById, artist) => {
            newArtistImagesById[artist?.id] = artist?.images?.[0]?.url;
            return newArtistImagesById;
          }, {}),
        })
      );
    }
  }, [artists]);

  const previousNumberOfTracks = useRef(null);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isSnackbarVisible, setIsSnackbarVisible] = useState(false);

  const enhancedArtistsRef = useRef(null);
  useEffect(() => {
    const enhancedArtists =
      nextTracks &&
      Object.values(
        nextTracks.reduce((artists, track) => {
          if (track?.isRecommendation) {
            const artist = track?.artists?.[0];
            if (!(artist?.uri in artists)) {
              artists[artist?.uri] = { artist, count: 0 };
            }
            artists[artist?.uri].count++;
          }
          return artists;
        }, {})
      )
        .sort((a, b) => b.count - a.count)
        .map(({ artist }) => artist)
        .filter(Boolean);

    if (enhancedArtists?.length) enhancedArtistsRef.current = enhancedArtists;

    const diff = nextTracks.length - previousNumberOfTracks.current;
    if (
      previousNumberOfTracks.current != null &&
      !isNaN(diff) &&
      Math.abs(diff) > 1 // Technically this should be 0, but then we'd get a snackbar when you skip, and I don't want to bother working around that :-)
    ) {
      setSnackbarMessage(
        `${diff < 0 ? 'Removed' : 'Added'} ${Math.abs(diff)} songs${
          enhancedArtistsRef.current?.length
            ? ` by ${enhancedArtistsRef.current
                .map((item) => item?.name)
                .filter(Boolean)
                .reduce((str, name) => {
                  if (!str.length || str.length + name.length < 19) {
                    if (str.length) {
                      str += ', ';
                    }
                    str += name;
                    if (str.length > 21) {
                      str = truncate(str, 19);
                    }
                  }
                  return str;
                }, '')}, and more`
            : ''
        }`
      );
    }
    previousNumberOfTracks.current = nextTracks.length;
  }, [nextTracks.map((track) => track?.uri).join('')]);

  useEffect(() => {
    if (snackbarMessage) {
      setIsSnackbarVisible(true);
      const timeout = setTimeout(() => {
        setIsSnackbarVisible(false);
      }, 6000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [snackbarMessage]);

  const items = isPremium ? nextTracks : artists;

  const contentHeight = 206;

  return (
    <div className="Queue">
      <Flipper
        flipKey={items?.map(({ uri }) => uri).join('')}
        spring="noWobble"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          width="375"
          height="50"
          fill="none"
          viewBox="0 0 375 50"
          style={{
            position: 'fixed',
            top: '0',
            backdropFilter: 'blur(20px)',
            transform: 'translate3d(0, 0, 0)',
            zIndex: 1,
          }}
          onClick={onClose}
        >
          <path fill="#121212" d="M0 0H375V50H0z" fillOpacity="0.7"></path>
          <text
            fill="#fff"
            style={{ whiteSpace: 'pre' }}
            fontSize="16"
            fontWeight="bold"
            letterSpacing="0em"
          >
            <tspan x="139.57" y="31.24">
              {contextName}
            </tspan>
          </text>
          <path
            fill="#fff"
            d="M30.354 20.353l-.708-.707L24 25.293l-5.647-5.647-.707.707L23.293 26l-5.647 5.646.707.708L24 26.707l5.646 5.647.708-.708L24.707 26l5.647-5.647z"
          ></path>
        </svg>

        <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          fill="none"
          viewBox={`0 0 375 ${contentHeight}`}
          style={{}}
        >
          <path fill="#121212" d={`M0 0H375V${contentHeight}H0z`}></path>
          <path
            fill="#B3B3B3"
            d="M345 120.5a1.5 1.5 0 10-.001 2.999A1.5 1.5 0 00345 120.5zm6 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm6 0a1.5 1.5 0 10-.001 2.999A1.5 1.5 0 00357 120.5z"
          ></path>
          <text
            fill="#B3B3B3"
            style={{ whiteSpace: 'pre' }}
            fontSize="13"
            fontWeight="450"
            letterSpacing="0em"
          >
            <tspan x={currentTrack.isRecommendation ? 94 : 76} y="138.257">
              {truncate(currentTrack?.artists?.[0].name, 30)}
            </tspan>
          </text>
          <text
            fill="#fff"
            style={{ whiteSpace: 'pre' }}
            fontSize="16"
            fontWeight="450"
            letterSpacing="0em"
          >
            <tspan x="76" y="119.24">
              {truncate(currentTrack?.name, 30)}
            </tspan>
          </text>

          {currentTrack?.isRecommendation && (
            <g transform="translate(76, 128)">
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M0 2C0 0.895431 0.895431 0 2 0H10C11.1046 0 12 0.895431 12 2V10C12 11.1046 11.1046 12 10 12H2C0.895431 12 0 11.1046 0 10V2ZM4.18906 4.62L5.0504 6.94772L7.37812 7.80906L5.0504 8.6704L4.18906 10.9981L3.32772 8.6704L1 7.80906L3.32772 6.94772L4.18906 4.62ZM9.0378 2.74579L8.3918 1L7.74579 2.74579L6 3.3918L7.74579 4.0378L8.3918 5.78359L9.0378 4.0378L10.7836 3.3918L9.0378 2.74579Z"
                fill="#1DB954"
              />
            </g>
          )}

          <path
            fill={`url(#${currentTrackImageUrl})`}
            d="M16 98H64V146H16z"
          ></path>

          <text
            fill="#fff"
            style={{ whiteSpace: 'pre' }}
            fontSize="18"
            fontWeight="bold"
            letterSpacing="0em"
          >
            <tspan x="16" y="75.895">
              Now playing
            </tspan>
          </text>
          <text
            fill="#fff"
            style={{ whiteSpace: 'pre' }}
            fontSize="18"
            fontWeight="bold"
            letterSpacing="0em"
          >
            <tspan x="16" y="191.895">
              {isPremium ? 'Next' : 'More'} from: {contextName}
            </tspan>
          </text>

          <defs>
            <pattern
              id={currentTrackImageUrl}
              width="1"
              height="1"
              patternContentUnits="objectBoundingBox"
            >
              <image
                width="640"
                height="640"
                transform="scale(.00156)"
                xlinkHref={currentTrackImageUrl}
              />
            </pattern>

            <pattern
              id="no-artist-image"
              x="0"
              y="0"
              width="104"
              height="104"
              patternUnits="userSpaceOnUse"
            >
              <rect x="0" y="0" width="104" height="104" fill="#333" />
              <g transform="translate(44, 48)">
                <path
                  d="M32.88 29.393L30.001 27.738C29.647 27.535 29.544 27.214 29.514 27.042C29.484 26.869 29.474 26.533 29.739 26.223L32.36 23.15C34.062 21.161 35 18.622 35 16.001V15.001C35 11.973 33.732 9.047 31.521 6.975C29.279 4.873 26.345 3.83 23.269 4.025C17.51 4.399 12.999 9.371 12.999 15.344V16C12.999 18.621 13.937 21.16 15.638 23.148L18.26 26.222C18.525 26.532 18.515 26.868 18.485 27.041C18.456 27.214 18.352 27.534 17.999 27.737L11.572 31.43C8.134 33.383 6 37.051 6 41V44H8V41.002C8 37.771 9.747 34.77 12.565 33.168L18.997 29.473C19.772 29.027 20.305 28.265 20.457 27.383C20.609 26.501 20.364 25.605 19.783 24.924L17.16 21.849C15.767 20.222 15.001 18.145 15.001 16V15.343C15.001 10.42 18.69 6.325 23.399 6.02C25.925 5.848 28.319 6.713 30.154 8.433C31.99 10.154 33.001 12.486 33.001 15V16C33.001 18.145 32.234 20.222 30.841 21.85L28.219 24.924C27.638 25.605 27.393 26.501 27.545 27.383C27.697 28.265 28.231 29.027 29.007 29.473L30.659 30.423C31.349 29.996 32.091 29.644 32.88 29.393ZM40 23.254V34.506C38.92 33.525 37.492 32.922 35.922 32.922C32.57 32.922 29.844 35.648 29.844 39C29.844 42.352 32.571 45.078 35.922 45.078C39.273 45.077 42 42.351 42 39V26.718L46.035 29.047L47.035 27.315L40 23.254ZM35.922 43.077C33.673 43.077 31.844 41.248 31.844 38.999C31.844 36.75 33.674 34.921 35.922 34.921C38.17 34.922 40 36.751 40 39C40 41.249 38.17 43.077 35.922 43.077Z"
                  fill="white"
                />
              </g>
            </pattern>
          </defs>
        </svg>

        {isPremium ? (
          nextTracks?.map((track, i) => (
            <Flipped
              key={track?.uri}
              flipId={track?.uri}
              onAppear={onAppear}
              onExit={onExit}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 375 64"
              >
                <g transform="translate(0, -207)">
                  <path
                    fill="#fff"
                    fillOpacity="0.3"
                    d="M338 233.502v1.5h18v-1.5h-18zm0 5.998h18V238h-18v1.5zm0 4.5h18v-1.5h-18v1.5z"
                  ></path>
                  <text
                    fill="#B3B3B3"
                    style={{ whiteSpace: 'pre' }}
                    fontSize="13"
                    fontWeight="450"
                    letterSpacing="0em"
                  >
                    <tspan x={track?.isRecommendation ? 94 : 76} y="254.257">
                      {truncate(track?.artists?.[0].name, 30)}
                    </tspan>
                  </text>
                  <text
                    fill="#fff"
                    style={{ whiteSpace: 'pre' }}
                    fontSize="16"
                    fontWeight="450"
                    letterSpacing="0em"
                  >
                    <tspan x="76" y="235.24">
                      {truncate(track?.name, 30)}
                    </tspan>
                  </text>
                  <path
                    fill="#535353"
                    d="M38 248.5c-5.799 0-10.5-4.701-10.5-10.5h-3c0 7.456 6.044 13.5 13.5 13.5v-3zM48.5 238c0 5.799-4.701 10.5-10.5 10.5v3c7.456 0 13.5-6.044 13.5-13.5h-3zM38 227.5c5.799 0 10.5 4.701 10.5 10.5h3c0-7.456-6.044-13.5-13.5-13.5v3zm0-3c-7.456 0-13.5 6.044-13.5 13.5h3c0-5.799 4.701-10.5 10.5-10.5v-3z"
                  ></path>
                </g>

                {track?.isRecommendation && (
                  <g transform="translate(77, 36.5)">
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M0 2C0 0.895431 0.895431 0 2 0H10C11.1046 0 12 0.895431 12 2V10C12 11.1046 11.1046 12 10 12H2C0.895431 12 0 11.1046 0 10V2ZM4.18906 4.62L5.0504 6.94772L7.37812 7.80906L5.0504 8.6704L4.18906 10.9981L3.32772 8.6704L1 7.80906L3.32772 6.94772L4.18906 4.62ZM9.0378 2.74579L8.3918 1L7.74579 2.74579L6 3.3918L7.74579 4.0378L8.3918 5.78359L9.0378 4.0378L10.7836 3.3918L9.0378 2.74579Z"
                      fill="#1DB954"
                    />
                  </g>
                )}
              </svg>
            </Flipped>
          ))
        ) : (
          <div className="Queue__artist-grid">
            {artists?.map((artist, i) => {
              const imageUrl = artistImagesById[artist.id];
              return (
                <Flipped
                  key={artist?.uri}
                  flipId={artist?.uri}
                  onAppear={onAppear}
                  onExit={onExit}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 118 176"
                  >
                    <g transform="translate(-9, 0)">
                      <circle
                        fill={`url(#${imageUrl || 'no-artist-image'})`}
                        cx={68}
                        cy={76}
                        r="52"
                      />

                      <text
                        fill="#fff"
                        style={{ whiteSpace: 'pre' }}
                        fontSize="13"
                        fontWeight="450"
                        letterSpacing="0"
                      >
                        <tspan x={68} y={153.257} textAnchor="middle">
                          {truncate(artist?.name, 16)}
                        </tspan>
                      </text>
                    </g>
                    {imageUrl && (
                      <defs>
                        <pattern
                          id={imageUrl}
                          width="1"
                          height="1"
                          patternContentUnits="objectBoundingBox"
                        >
                          <image
                            width="208"
                            height="208"
                            xlinkHref={imageUrl}
                            transform="scale(.0048)"
                            preserveAspectRatio="xMidYMin slice"
                          />
                        </pattern>
                      </defs>
                    )}
                  </svg>
                </Flipped>
              );
            })}
          </div>
        )}

        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 375 176"
          style={{
            position: 'fixed',
            bottom: 0,
            zIndex: 1,
          }}
        >
          <path fill="#121212" d="M0 64H375V176H0z"></path>

          <path
            fill="url(#queue-controls-paint0_linear)"
            d="M0 0H375V64H0z"
          ></path>
          <path
            fill="#fff"
            d="M335 98h8v2l4.763-2.75L343 94.5V97h-8c-3.85 0-7 3.15-7 7 0 1.259.342 2.44.93 3.464l.862-.498A5.945 5.945 0 01329 104c0-3.309 2.692-6 6-6zm14.07 2.537l-.861.498c.501.875.791 1.885.791 2.965 0 3.309-2.692 6-6 6h-8v-2l-4.763 2.75L335 113.5V111h8c3.85 0 7-3.15 7-7 0-1.259-.342-2.44-.93-3.463z"
            opacity="0.7"
          ></path>

          <g onClick={props.nextTrack}>
            <rect
              fill="#000"
              width="60"
              height="60"
              x="244"
              y="72"
              fillOpacity="0.001"
            />
            <path
              fill="#fff"
              d="M280 94v8.237l-14.5-8.371a1 1 0 00-1.5.866v18.536a1 1 0 001.5.866l14.5-8.371V114a1 1 0 001 1h2a1 1 0 001-1V94a1 1 0 00-1-1h-2a1 1 0 00-1 1z"
            ></path>
          </g>

          <g onClick={props.togglePlayback}>
            {isPlaying ? (
              <path
                fill="#fff"
                fillRule="evenodd"
                d="M188 136c17.673 0 32-14.327 32-32 0-17.673-14.327-32-32-32-17.673 0-32 14.327-32 32 0 17.673 14.327 32 32 32zm4-21c0 .6.4 1 1 1h4c.6 0 1-.4 1-1V93c0-.6-.4-1-1-1h-4c-.6 0-1 .4-1 1v22zm-9 1h-4c-.6 0-1-.4-1-1V93c0-.6.4-1 1-1h4c.6 0 1 .4 1 1v22c0 .6-.4 1-1 1z"
                clipRule="evenodd"
              ></path>
            ) : (
              <g transform="translate(156, 72)">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M64 32.3733C64 50.0464 49.6731 64.3733 32 64.3733C14.3269 64.3733 0 50.0464 0 32.3733C0 14.7002 14.3269 0.373291 32 0.373291C49.6731 0.373291 64 14.7002 64 32.3733ZM43.285 33.2393L25.5 43.5073C24.833 43.8923 24 43.4113 24 42.6413V22.1053C24 21.3353 24.833 20.8543 25.5 21.2393L43.285 31.5073C43.952 31.8923 43.952 32.8543 43.285 33.2393Z"
                  fill="white"
                />
              </g>
            )}
          </g>

          <g onClick={props.previousTrack}>
            <rect
              fill="#000"
              width="60"
              height="60"
              x="72"
              y="72"
              fillOpacity="0.001"
            />
            <path
              fill="#fff"
              d="M110.5 93.866L96 102.237V94a1 1 0 00-1-1h-2a1 1 0 00-1 1v20a1 1 0 001 1h2a1 1 0 001-1v-8.237l14.5 8.371a1 1 0 001.5-.866V94.732a1 1 0 00-1.5-.866z"
            ></path>
          </g>

          <g onClick={() => isPremium && props.toggleShuffle()}>
            <rect
              fill="#000"
              width="60"
              height="60"
              x="6"
              y="72"
              fillOpacity="0.001"
            />
            {isShuffleEnabled ? (
              <g transform="translate(24, 92)">
                <path
                  d="M6.184 8.16001L7.819 10.075L8.476 9.30501L6.944 7.51101C5.708 6.06301 3.906 5.23201 2.001 5.23201V6.23201C3.613 6.23201 5.138 6.93601 6.184 8.16001ZM22.763 17.792L18 15.042V17.292H17.836C17.836 17.292 17.836 17.292 17.835 17.292C16.222 17.292 14.697 16.589 13.651 15.364L12.422 13.925L11.764 14.695L12.89 16.013C14.127 17.461 15.93 18.291 17.835 18.291H17.836H18V20.541L22.763 17.792ZM17.835 6.70901H18V8.95801L22.763 6.20801L18 3.45801V5.70901H17.836C15.93 5.70901 14.128 6.53901 12.89 7.98701L6.184 15.84C5.137 17.065 3.612 17.768 2 17.768V18.768C3.905 18.768 5.707 17.937 6.943 16.49L13.65 8.63701C14.697 7.41101 16.222 6.70901 17.835 6.70901Z"
                  fill="#1DB954"
                />
                <circle cx="12" cy="30" r="2" fill="#1DB954" />
              </g>
            ) : (
              <path
                fill="#fff"
                d="M30.184 100.16l1.635 1.915.657-.77-1.532-1.794a6.49 6.49 0 00-4.943-2.279v1c1.612 0 3.137.704 4.183 1.928zm16.579 9.632L42 107.042v2.25h-.164-.001a5.494 5.494 0 01-4.184-1.928l-1.229-1.439-.658.77 1.126 1.318a6.495 6.495 0 004.945 2.278H42v2.25l4.763-2.749zm-4.928-11.083H42v2.249l4.763-2.75L42 95.458v2.251h-.164c-1.906 0-3.708.83-4.946 2.278l-6.706 7.853A5.497 5.497 0 0126 109.768v1a6.493 6.493 0 004.943-2.278l6.707-7.853a5.494 5.494 0 014.185-1.928z"
              ></path>
            )}
          </g>

          <defs>
            <linearGradient
              id="queue-controls-paint0_linear"
              x1="0"
              x2="0"
              y1="0"
              y2="64"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#121212" stopOpacity="0.01"></stop>
              <stop offset="1" stopColor="#121212"></stop>
            </linearGradient>
          </defs>
        </svg>
      </Flipper>

      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 375 64"
        style={{
          position: 'fixed',
          bottom: 0,
          zIndex: 2,
          transform: `translate3d(0, ${isSnackbarVisible ? 0 : '100%'}, 0)`,
          willChange: 'transform',
          transition: 'transform 0.3s ease-in-out',
        }}
        onClick={() => setIsSnackbarVisible(false)}
      >
        <g filter="url(#snackbar-filter0_d)">
          <rect width="359" height="48" x="8" y="8" fill="#fff" rx="4"></rect>
        </g>
        <text
          fill="#121212"
          style={{ whiteSpace: 'pre' }}
          fontSize="13"
          letterSpacing="0em"
        >
          <tspan x="24" y="36.258">
            {snackbarMessage}
          </tspan>
        </text>
        <defs>
          <filter
            id="snackbar-filter0_d"
            width="367"
            height="56"
            x="4"
            y="8"
            colorInterpolationFilters="sRGB"
            filterUnits="userSpaceOnUse"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
            <feColorMatrix
              in="SourceAlpha"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            ></feColorMatrix>
            <feOffset dy="4"></feOffset>
            <feGaussianBlur stdDeviation="2"></feGaussianBlur>
            <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0"></feColorMatrix>
            <feBlend
              in2="BackgroundImageFix"
              result="effect1_dropShadow"
            ></feBlend>
            <feBlend
              in="SourceGraphic"
              in2="effect1_dropShadow"
              result="shape"
            ></feBlend>
          </filter>
        </defs>
      </svg>
    </div>
  );
});
